import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import { GripVertical, Settings2, Loader2, RotateCcw } from 'lucide-react';

const SortableItem = ({ column, onToggleVisible }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: column.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Don't allow hiding/reordering "actions" column only
  const isFixed = column.id === 'actions';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 bg-slate-800 rounded-sm mb-2 ${
        isFixed ? 'opacity-60' : ''
      }`}
    >
      <button
        {...attributes}
        {...listeners}
        className={`cursor-grab active:cursor-grabbing text-slate-500 hover:text-slate-300 ${
          isFixed ? 'invisible' : ''
        }`}
        disabled={isFixed}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <Checkbox
        checked={column.visible}
        onCheckedChange={(checked) => onToggleVisible(column.id, checked)}
        disabled={isFixed}
        className="border-slate-600"
      />
      <span className={`text-sm ${column.visible ? 'text-white' : 'text-slate-500'}`}>
        {column.label || column.id}
      </span>
    </div>
  );
};

export const ColumnSettingsModal = ({ open, onOpenChange, columns, onSave, loading }) => {
  const [localColumns, setLocalColumns] = useState(columns);

  React.useEffect(() => {
    setLocalColumns(columns);
  }, [columns]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // 5px movement to start drag
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setLocalColumns((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleToggleVisible = (id, visible) => {
    setLocalColumns((items) =>
      items.map((item) =>
        item.id === id ? { ...item, visible } : item
      )
    );
  };

  const handleSave = async () => {
    await onSave(localColumns);
    onOpenChange(false);  // Close modal after save
  };

  const handleReset = () => {
    const defaultColumns = [
      { id: 'marker', label: 'Marker', visible: true, width: 8 },
      { id: 'important', label: 'Important', visible: true, width: 40 },
      { id: 'actions', label: 'Actions', visible: true, width: 120 },
      { id: 'container_number', label: 'Container #', visible: true, width: 140 },
      { id: 'container_size', label: 'Type', visible: true, width: 70 },
      { id: 'ssl', label: 'SSL', visible: true, width: 80 },
      { id: 'chassis', label: 'Chassis', visible: true, width: 100 },
      { id: 'job_number', label: 'Job #', visible: true, width: 100 },
      { id: 'pp_ctf', label: 'PP/CTF', visible: true, width: 70 },
      { id: 'status', label: 'Status', visible: false, width: 150 },
      { id: 'eta', label: 'ETA', visible: true, width: 100 },
      { id: 'appointment', label: 'Appt', visible: true, width: 100 },
      { id: 'overweight', label: 'OW', visible: true, width: 80 },
      { id: 'hazmat', label: 'HZ', visible: true, width: 80 },
      { id: 'mty_rdy', label: 'MTY RDY', visible: true, width: 100 },
      { id: 'mty_return', label: 'MTY Return', visible: true, width: 100 },
      { id: 'empty_return', label: 'Empty Return', visible: true, width: 120 },
      { id: 'lfd_yard', label: 'LFD Yard', visible: true, width: 100 },
      { id: 'customer', label: 'Customer', visible: true, width: 150 },
      { id: 'deliver_to', label: 'Deliver To', visible: true, width: 200 },
      { id: 'driver', label: 'Driver', visible: true, width: 100 },
      { id: 'docs', label: 'Docs', visible: true, width: 60 },
      { id: 'remarks', label: 'Remarks', visible: true, width: 150 }
    ];
    setLocalColumns(defaultColumns);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-slate-900 border-slate-800" data-testid="column-settings-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-white flex items-center gap-2">
            <Settings2 className="h-5 w-5" />
            Column Settings
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Drag to reorder columns. Uncheck to hide.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[400px] overflow-y-auto py-4">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={localColumns.map((c) => c.id)}
              strategy={verticalListSortingStrategy}
            >
              {localColumns.map((column) => (
                <SortableItem
                  key={column.id}
                  column={column}
                  onToggleVisible={handleToggleVisible}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>

        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleReset}
            className="border-slate-700"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-slate-700"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-500"
            data-testid="save-columns-btn"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ColumnSettingsModal;
